import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

interface TaskQuickAddProps {
  onAdd: (title: string) => Promise<void>;
  isLoading?: boolean;
  placeholder?: string;
}

export const TaskQuickAdd: React.FC<TaskQuickAddProps> = ({
  onAdd,
  isLoading = false,
  placeholder = 'Add a new task...',
}) => {
  const [title, setTitle] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || isLoading) return;

    try {
      await onAdd(trimmedTitle);
      setTitle('');
    } catch (error) {
      // Error handling is done by the parent
      console.error('Error adding task:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <AddIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : title.trim() ? (
            <InputAdornment position="end">
              <Tooltip title="Press Enter to add">
                <IconButton
                  type="submit"
                  size="small"
                  color="primary"
                  disabled={isLoading}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
          },
        }}
      />
    </Box>
  );
};

export default TaskQuickAdd;
